import {
  Container,
  GhostButton,
  Layout,
  PrimaryButton,
  ProgressLoader,
  SecondaryTopNav,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { t } from "i18next";
import { PropsWithChildren, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { CreateUserContext } from "./CreateUserContext";
import { PageTitle } from "../../../../components/page-title/PageTitle";

interface Props extends PropsWithChildren {
  disableCompletion?: boolean;
  submitting?: boolean;
  onComplete?: () => void;
}

export function CreateUserPageLayout(props: Props) {
  const { disableCompletion, submitting, onComplete, children } = props;
  const navigate = useNavigate();
  const { goToPrevStep, currentStep } = useContext(CreateUserContext);
  const steps = [t("Create New User"), t("Assign user to group(s)")];
  const atLastStep = currentStep >= steps.length - 1;
  const isFirstStep = currentStep === 0;
  const readableCurrentStep = currentStep + 1;

  return (
    <Layout
      layout="narrow"
      header={
        <SecondaryTopNav
          testID="close-button"
          leftIcon={isFirstStep ? "close" : "arrow_back"}
          onleftIconOnPress={
            isFirstStep ? () => navigate("/settings/users") : goToPrevStep
          }
        >
          {currentStep > 0 && (
            <GhostButton
              onPress={() => navigate("/settings/users")}
              size="medium"
              testID="previous-step-button"
            >
              {t("Cancel")}
            </GhostButton>
          )}

          <PrimaryButton
            onPress={onComplete}
            isDisabled={disableCompletion}
            isLoading={submitting}
            testID={atLastStep ? "complete-user-button" : "next-step-button"}
          >
            {atLastStep ? t("Complete user") : t("Next step")}
          </PrimaryButton>
        </SecondaryTopNav>
      }
    >
      <Container
        testID={
          steps[currentStep].replaceAll(" ", "-").toLowerCase() + "-container"
        }
      >
        <div className={styles.container}>
          <div>
            <Text style="input/section-title">
              {t(`step {{readableCurrentStep}} of {{steps}}`, {
                readableCurrentStep,
                steps: steps.length,
              })}
            </Text>
            <PageTitle>{steps[currentStep]}</PageTitle>
          </div>
          <div style={{ paddingBottom: "var(--space-3)" }}>
            <ProgressLoader floating value={currentStep === 0 ? 0 : 50} />
          </div>
          {children}
        </div>
      </Container>
    </Layout>
  );
}

const styles = {
  container: css({
    paddingLeft: "var(--space-8)",
    paddingRight: "var(--space-8)",
    paddingTop: "var(--space-7)",
    paddingBottom: "var(--space-7)",
    gap: "var(--space-3)",
    display: "flex",
    flexDirection: "column",
  }),
};
